import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg, fmtDate, usageLabel, channelLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { StatusBadge } from "@/components/StatusBadge";
import { ShieldCheck, Search, Loader2, BadgeCheck, XCircle, Camera } from "lucide-react";

const Row = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-1.5">
    <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className="text-right text-sm font-medium">{children}</span>
  </div>
);

export default function VerifyRegistry() {
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get("id") || "");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    const id = query.trim();
    if (!id) return;
    setBusy(true);
    setResult(null);
    setError(null);
    try {
      const { data } = await api.get(`/registry/${encodeURIComponent(id)}`);
      setResult(data);
    } catch (err) {
      setError(errMsg(err, "No licence found for this ID"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hero-mist min-h-[calc(100vh-4rem)]">
      <div className="page-container max-w-2xl py-14">
        <div className="text-center">
          <ShieldCheck className="mx-auto h-10 w-10 text-[hsl(174,38%,34%)]" />
          <h1 className="mt-4 font-display text-3xl tracking-[-0.01em] sm:text-4xl">Ethical AI Registry</h1>
          <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
            Every LOOKBOOK licence has a public Registry ID. Enter it below to verify that the model consented,
            was paid upfront, and that the campaign is within its licensed scope and time period.
          </p>
        </div>

        <form onSubmit={lookup} className="mt-8 flex gap-2">
          <Input
            data-testid="registry-search-input"
            placeholder="Enter Registry ID (e.g. 5F2A81C3\u2026)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="rounded-xl bg-card font-mono-plex"
          />
          <Button data-testid="registry-search-button" type="submit" disabled={busy || !query.trim()} className="rounded-xl">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span className="ml-1.5">Verify</span>
          </Button>
        </form>

        {error && (
          <Card data-testid="registry-not-found" className="mt-6 rounded-2xl border-2 border-[hsl(6,62%,72%)]">
            <CardContent className="flex items-start gap-3 p-6">
              <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <div>
                <p className="font-semibold">Not found in the registry</p>
                <p className="text-sm text-muted-foreground">
                  {error} — if a brand claims their AI imagery is LOOKBOOK-licensed but the ID cannot be verified,
                  the usage may be unauthorised.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {result && (
          <Card data-testid="registry-result-card" className="mt-6 rounded-2xl border-2 border-[hsl(174,38%,70%)]">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <BadgeCheck className="h-6 w-6 text-[hsl(174,38%,34%)]" />
                  <div>
                    <p className="font-display text-xl leading-tight">Ethically Sourced AI</p>
                    <p className="font-mono-plex text-[10px] text-muted-foreground">{result.registry_id}</p>
                  </div>
                </div>
                <StatusBadge status={result.status} />
              </div>
              <Separator className="my-4" />
              <Row label="Model">{result.model_name}</Row>
              <Row label="Licensed to">{result.licensed_to}</Row>
              <Row label="Valid period">{fmtDate(result.valid_from)} → {fmtDate(result.valid_until)}</Row>
              <Row label="Usage">{usageLabel(result.terms?.usage_type)}</Row>
              <Row label="Retouching">{result.terms?.retouching_allowed ? "Consented (minor only)" : "Not permitted"}</Row>
              <Row label="Format"><span className="inline-flex items-center gap-1"><Camera className="h-3.5 w-3.5" /> Static images only</span></Row>
              {(result.terms?.media_channels || []).length > 0 && (
                <div className="py-1.5">
                  <p className="mb-1.5 text-xs uppercase tracking-wide text-muted-foreground">Licensed media</p>
                  <div className="flex flex-wrap gap-1.5">
                    {result.terms.media_channels.map((c) => (
                      <Badge key={c} variant="outline" className="rounded-full border-[hsl(174,38%,60%)] text-[10px]">{channelLabel(c)}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <Separator className="my-4" />
              <div className="grid gap-2 sm:grid-cols-3">
                {[
                  { ok: result.consent?.model_consented, label: "Model consented" },
                  { ok: result.paid_upfront, label: "Paid upfront" },
                  { ok: result.consent?.ai_ethics_screened, label: "AI ethics screened" },
                ].map((f) => (
                  <div key={f.label} className="flex items-center gap-1.5 rounded-xl bg-[hsl(174,38%,96%)] px-3 py-2 text-xs font-medium">
                    <ShieldCheck className="h-3.5 w-3.5 text-[hsl(174,38%,34%)]" /> {f.label}
                  </div>
                ))}
              </div>
              {result.consent?.consented_at && (
                <p className="mt-3 font-mono-plex text-[10px] text-muted-foreground">
                  Consent recorded {fmtDate(result.consent.consented_at)} · Agreement v{result.agreement_version}
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
